import {
  all,
  takeEvery,
  takeLatest,
  put,
  call,
  select,
} from 'redux-saga/effects';
import { timerActionTypes } from './Timer.actionTypes';
import teamDatabase from '../../services/team';
import { getUserTimezone } from '../../utils/timezone';
import { getServerTimeISO, getServerTime } from '../../services/serverTime';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import {
  generateSessionId,
  splitSessionByDates,
  validateSessionDuration,
} from '../../utils/timeUtils';

// Start Timer Saga
export function* startTimerSaga({ payload }) {
  yield put({ type: timerActionTypes.START_TIMER_PENDING });

  try {
    const { timerSession, teamId, userId, companyId } = payload;

    console.log('🔴 START_TIMER: Starting timer with payload', {
      teamId,
      userId,
      companyId,
      timestamp: new Date().toISOString(),
    });

    // // Check if user is punched in before starting timer
    // const timerState = yield select((state) => state.timer);
    // if (!timerState.punchStatus?.isPunchedIn) {
    //   const errorMessage = 'Please punch in first before starting the timer.';
    //   addNotification({
    //     message: errorMessage,
    //     level: notificationStates.error,
    //   });
    //   yield put({
    //     type: timerActionTypes.START_TIMER_FAILED,
    //     payload: { error: errorMessage }
    //   });
    //   return;
    // }

    // Validate required fields
    if (!timerSession.projectId || !timerSession.taskName) {
      const errorMessage =
        'Please select a project and task name before starting the timer.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.START_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Validate team and user data
    if (!teamId || !userId) {
      const errorMessage =
        'Unable to start timer. Please ensure you are logged in and have selected a team.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.START_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Get user timezone from database
    console.log('🔴 START_TIMER: Getting user timezone from database');
    let userTimezoneInfo;
    try {
      userTimezoneInfo = yield call(getUserTimezone, teamId, userId);
      console.log('🔴 START_TIMER: Timezone retrieved from database', {
        timezone: userTimezoneInfo.timezone,
        source: userTimezoneInfo.source,
      });
    } catch (error) {
      const errorMessage = `Unable to get user timezone: ${error.message}`;
      console.error('🔴 START_TIMER: Timezone error', { error: error.message });
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.START_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Check for cross-platform conflicts before starting
    const crossPlatformCheck = yield call(
      teamDatabase.checkExistingActiveSession,
      teamId,
      userId
    );

    if (
      crossPlatformCheck.success &&
      crossPlatformCheck.exists &&
      crossPlatformCheck.data
    ) {
      const existingSession = crossPlatformCheck.data;

      // Check if the existing session is from a different platform
      if (existingSession.source && existingSession.source !== 'web') {
        const errorMessage = `You cannot start timer as it is already running in ${
          existingSession.source === 'desktop'
            ? 'desktop app'
            : 'another platform'
        }`;

        addNotification({
          message: errorMessage,
          level: notificationStates.error,
        });

        yield put({
          type: timerActionTypes.START_TIMER_FAILED,
          payload: { error: errorMessage },
        });
        return;
      }
    } else if (!crossPlatformCheck.success) {
      // Database check failed - BLOCK operation as requested
      const errorMessage =
        'Unable to verify timer status. Please check your connection and try again.';

      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });

      yield put({
        type: timerActionTypes.START_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Save active session to database with locked timezone and session ID
    const startTime = yield call(getServerTimeISO);
    const sessionTimezone = userTimezoneInfo.timezone; // Use database timezone
    const sessionId = generateSessionId();

    console.log('🔴 START_TIMER: Creating session with database timezone', {
      startTime,
      sessionTimezone,
      sessionId,
    });

    const sessionData = {
      ...timerSession,
      sessionId,
      startTime,
      sessionTimezone,
      isActive: true,
      companyId: companyId || '',
      userId: userId,
    };

    const response = yield call(
      teamDatabase.saveActiveTimerSession,
      teamId,
      userId,
      sessionData
    );

    if (response.success) {
      // Store start timestamp in localStorage
      localStorage.setItem(
        `timer_start_${teamId}_${userId}`,
        Date.now().toString()
      );

      yield put({
        type: timerActionTypes.START_TIMER_SUCCESS,
        payload: { sessionData, startTime },
      });

      // Refresh punch status to update activeSession state in Redux
      yield put({
        type: timerActionTypes.CHECK_PUNCH_STATUS,
        payload: { teamId, userId },
      });

      console.log('Timer session saved successfully with database timezone');
      addNotification({
        message: 'Timer started successfully!',
        level: notificationStates.success,
      });
    } else {
      const errorMessage = 'Failed to start timer. Please try again.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.START_TIMER_FAILED,
        payload: { error: errorMessage },
      });
    }
  } catch (error) {
    console.error('Error starting timer:', error);
    const errorMessage = 'Failed to start timer. Please try again.';
    addNotification({
      message: errorMessage,
      level: notificationStates.error,
    });
    yield put({
      type: timerActionTypes.START_TIMER_FAILED,
      payload: { error: errorMessage },
    });
  }
}

// Stop Timer Saga - Implements midnight splitting
export function* stopTimerSaga({ payload }) {
  yield put({ type: timerActionTypes.STOP_TIMER_PENDING });

  try {
    const { teamId, userId, companyId } = payload;

    console.log('🔴 STOP_TIMER: Starting timer stop process', {
      teamId,
      userId,
      companyId,
      timestamp: new Date().toISOString(),
    });

    // Get active session from activeSessions collection
    const activeSessionResponse = yield call(
      teamDatabase.getActiveTimerSession,
      teamId,
      userId
    );

    console.log('🔴 STOP_TIMER: Active session response', {
      success: activeSessionResponse.success,
      exists: activeSessionResponse.exists,
      sessionData: activeSessionResponse.data,
    });

    if (!activeSessionResponse.success || !activeSessionResponse.exists) {
      const errorMessage =
        'No active timer session found. Please start the timer first.';
      console.log('🔴 STOP_TIMER: ERROR - No active session found');
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.STOP_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }
    // Get active session data
    const activeSession = activeSessionResponse.data;
    // Validate if start time is present
    if (!activeSession.startTime) {
      const errorMessage =
        'Invalid session data: No start time found. Please restart the timer.';
      console.log('🔴 STOP_TIMER: ERROR - No start time in session', {
        sessionData: activeSession,
      });
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.STOP_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }
    // Get end time and start time
    const endTime = yield call(getServerTime);
    const startTime = new Date(activeSession.startTime);
    const sessionTimezone = activeSession.sessionTimezone; // Always use stored timezone from session - no fallback

    // Validate that sessionTimezone exists
    if (!sessionTimezone) {
      const errorMessage =
        'Invalid session data: No timezone found in session. Please restart the timer.';
      console.log('🔴 STOP_TIMER: ERROR - No timezone in session', {
        sessionData: activeSession,
      });
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.STOP_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    console.log('🔴 STOP_TIMER: Timer session details', {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      sessionTimezone,
      startTimeLocal: startTime.toString(),
      endTimeLocal: endTime.toString(),
      durationMs: endTime.getTime() - startTime.getTime(),
      durationMinutes: Math.floor(
        (endTime.getTime() - startTime.getTime()) / 60000
      ),
      timezoneSource: 'session_database',
    });

    // Validate session duration
    // If session duration is less than 1 minute, block stopping
    const validation = validateSessionDuration(startTime, endTime);
    console.log('🔴 STOP_TIMER: Session validation result', {
      isValid: validation.isValid,
      message: validation.message,
      durationSeconds: validation.durationSeconds,
    });

    if (!validation.isValid) {
      addNotification({
        message: validation.message,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.STOP_TIMER_FAILED,
        payload: { error: validation.message },
      });
      return;
    }

    // Check if session crosses midnight and split if necessary
    const sessionData = {
      sessionId: activeSession.sessionId,
      companyId: activeSession.companyId || companyId || '',
      projectId: activeSession.projectId,
      taskId: activeSession.taskId || '',
      taskName: activeSession.taskName,
      taskDetails: activeSession.taskDetails || '',
      userId: userId || '',
    };

    console.log('🔴 STOP_TIMER: About to call splitSessionByDates with:', {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      sessionTimezone,
      sessionData,
    });

    const splitEntries = splitSessionByDates(
      startTime,
      endTime,
      sessionTimezone,
      sessionData
    );

    console.log('🔴 STOP_TIMER: Split entries result', {
      numberOfEntries: splitEntries.length,
      splitEntries: splitEntries.map((entry) => ({
        date: entry.date,
        timestampKey: entry.timestampKey,
        taskHours: entry.entry.taskHours,
        taskMinutes: entry.entry.taskMinutes,
        isSplit: entry.entry.isSplit,
        splitIndex: entry.entry.splitIndex,
        totalSplits: entry.entry.totalSplits,
      })),
    });

    let timelogResponse;

    if (splitEntries.length === 1) {
      // Single day session - use existing method
      const entry = splitEntries[0];
      const timelogData = {
        [entry.timestampKey]: entry.entry,
      };

      console.log('🔴 STOP_TIMER: Saving single day entry', {
        date: entry.date,
        timestampKey: entry.timestampKey,
        timelogData,
      });

      timelogResponse = yield call(
        teamDatabase.saveTimelogEntry,
        teamId,
        userId,
        entry.date,
        timelogData
      );
    } else {
      // Multi-day session - use new batch method
      console.log('🔴 STOP_TIMER: Saving multi-day entries using batch method');

      timelogResponse = yield call(
        teamDatabase.saveTimelogEntriesToMultipleDays,
        teamId,
        userId,
        splitEntries
      );

      console.log('🔴 STOP_TIMER: Multi-day save response', timelogResponse);

      // Show split notification
      addNotification({
        message: `Timer session split across ${splitEntries.length} days`,
        level: notificationStates.info,
      });
    }

    console.log('🔴 STOP_TIMER: Final timelog save response', {
      success: timelogResponse.success,
      savedCount: timelogResponse.savedCount,
      error: timelogResponse.error,
    });

    if (timelogResponse.success) {
      // Clear active session
      yield call(teamDatabase.clearActiveTimerSession, teamId, userId);

      // Remove start timestamp from localStorage
      localStorage.removeItem(`timer_start_${teamId}_${userId}`);

      const totalSeconds = Math.floor((endTime - startTime) / 1000);
      const taskHours = Math.floor(totalSeconds / 3600);
      const taskMinutes = Math.floor((totalSeconds % 3600) / 60);

      console.log('🔴 STOP_TIMER: Timer stopped successfully', {
        totalSeconds,
        taskHours,
        taskMinutes,
        splitEntriesCount: splitEntries.length,
        isSplit: splitEntries.length > 1,
      });

      yield put({
        type: timerActionTypes.STOP_TIMER_SUCCESS,
        payload: {
          splitEntries,
          totalSeconds,
          taskHours,
          taskMinutes,
          isSplit: splitEntries.length > 1,
        },
      });

      // Refresh punch status to update activeSession state in Redux
      yield put({
        type: timerActionTypes.CHECK_PUNCH_STATUS,
        payload: { teamId, userId },
      });

      console.log('Timer stopped and timelog saved successfully', {
        splitCount: splitEntries.length,
        totalSeconds,
      });

      addNotification({
        message: `Timer stopped and time log saved successfully${
          splitEntries.length > 1
            ? ` (split across ${splitEntries.length} days)`
            : ''
        }!`,
        level: notificationStates.success,
      });
    } else {
      console.log('🔴 STOP_TIMER: ERROR - Failed to save timelog', {
        error: timelogResponse.error,
        response: timelogResponse,
      });
      const errorMessage = 'Failed to save time log. Please try again.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.STOP_TIMER_FAILED,
        payload: { error: errorMessage },
      });
    }
  } catch (error) {
    console.error('🔴 STOP_TIMER: Exception occurred', {
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
    });
    const errorMessage = 'Failed to stop timer. Please try again.';
    addNotification({
      message: errorMessage,
      level: notificationStates.error,
    });
    yield put({
      type: timerActionTypes.STOP_TIMER_FAILED,
      payload: { error: errorMessage },
    });
  }
}

// Resume Timer Saga (Check for active session) - NEW: Simplified with activeSessions collection
export function* resumeTimerSaga({ payload }) {
  yield put({ type: timerActionTypes.RESUME_TIMER_PENDING });

  try {
    const { teamId, userId } = payload;

    if (!teamId || !userId) {
      yield put({
        type: timerActionTypes.RESUME_TIMER_FAILED,
        payload: { error: 'Invalid team or user data' },
      });
      return;
    }

    // Check for cross-platform conflicts before resuming - now uses activeSessions collection
    const crossPlatformCheck = yield call(
      teamDatabase.checkExistingActiveSession,
      teamId,
      userId
    );

    if (
      crossPlatformCheck.success &&
      crossPlatformCheck.exists &&
      crossPlatformCheck.data
    ) {
      const existingSession = crossPlatformCheck.data;

      // Check if the existing session is from a different platform
      if (existingSession.source && existingSession.source !== 'web') {
        // For auto-resume, just fail silently without showing notification
        yield put({
          type: timerActionTypes.RESUME_TIMER_FAILED,
          payload: {
            error: `Timer is already running in ${
              existingSession.source === 'desktop'
                ? 'desktop app'
                : 'another platform'
            }`,
            silent: true, // Flag to indicate no notification should be shown
          },
        });
        return;
      }

      // Session is from web platform - proceed with resume
      const sessionData = existingSession;
      const startTime = new Date(sessionData.startTime);
      const currentTime = yield call(getServerTime);
      const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);

      console.log('Active session found, resuming timer:', {
        sessionId: sessionData.sessionId,
        startTime: sessionData.startTime,
        elapsedSeconds,
        sessionTimezone: sessionData.sessionTimezone,
      });

      yield put({
        type: timerActionTypes.RESUME_TIMER_SUCCESS,
        payload: {
          sessionData: { ...sessionData, isActive: true },
          elapsedSeconds: Math.max(0, elapsedSeconds),
        },
      });
    } else if (!crossPlatformCheck.success) {
      // Database check failed - For auto-resume, fail silently
      yield put({
        type: timerActionTypes.RESUME_TIMER_FAILED,
        payload: {
          error: 'Unable to verify timer status',
          silent: true,
        },
      });
      return;
    } else {
      // No active session found
      yield put({
        type: timerActionTypes.RESUME_TIMER_FAILED,
        payload: { error: 'No active session found' },
      });
    }
  } catch (error) {
    console.error('Error resuming timer:', error);
    yield put({
      type: timerActionTypes.RESUME_TIMER_FAILED,
      payload: { error: 'Failed to resume timer session' },
    });
  }
}

export function* resumeTimerOnTabChangeSaga({ payload }) {
  yield put({ type: timerActionTypes.RESUME_TIMER_PENDING });

  try {
    const { teamId, userId } = payload;

    if (!teamId || !userId) {
      yield put({
        type: timerActionTypes.RESUME_TIMER_FAILED,
        payload: { error: 'Invalid team or user data' },
      });
      return;
    }

    // Get existing session data from Redux state
    const timerState = yield select((state) => state.timer);
    const sessionData = timerState.timerSession;

    if (!sessionData) {
      yield put({
        type: timerActionTypes.RESUME_TIMER_FAILED,
        payload: { error: 'No timer session found' },
      });
      return;
    }

    // Get start timestamp from localStorage
    const startTimestamp = localStorage.getItem(
      `timer_start_${teamId}_${userId}`
    );
    let startTime;

    if (!startTimestamp) {
      // Fallback: Get start time from active session in Redux
      const activeSession = timerState.punchStatus?.activeSession?.data;
      if (activeSession && activeSession.startTime) {
        startTime = new Date(activeSession.startTime).getTime();
        console.log(
          'Using fallback start time from active session:',
          startTime
        );
      } else {
        yield put({
          type: timerActionTypes.RESUME_TIMER_FAILED,
          payload: { error: 'No timer session found' },
        });
        return;
      }
    } else {
      startTime = parseInt(startTimestamp);
    }

    // Calculate elapsed time locally
    const currentTime = Date.now();
    const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);

    console.log('Resuming timer on tab change:', {
      startTimestamp: startTime,
      currentTime,
      elapsedSeconds,
      source: startTimestamp ? 'localStorage' : 'activeSession',
    });

    yield put({
      type: timerActionTypes.RESUME_TIMER_SUCCESS,
      payload: {
        sessionData: { ...sessionData, isActive: true },
        elapsedSeconds: Math.max(0, elapsedSeconds),
      },
    });
  } catch (error) {
    console.error('Error resuming timer on tab change:', error);
    yield put({
      type: timerActionTypes.RESUME_TIMER_FAILED,
      payload: { error: 'Failed to resume timer on tab change' },
    });
  }
}

// Cancel Timer Saga - NEW: Updated for separate punch/timer logic
export function* cancelTimerSaga({ payload }) {
  yield put({ type: timerActionTypes.CANCEL_TIMER_PENDING });

  try {
    const { teamId, userId } = payload;

    // NEW: Check for active session existence, not punch status for cancel
    const activeSessionResponse = yield call(
      teamDatabase.getActiveTimerSession,
      teamId,
      userId
    );

    if (!activeSessionResponse.success) {
      const errorMessage = 'Unable to verify timer session. Please try again.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.CANCEL_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    if (!activeSessionResponse.exists) {
      const errorMessage = 'No active timer session to cancel.';
      addNotification({
        message: errorMessage,
        level: notificationStates.warning,
      });
      yield put({
        type: timerActionTypes.CANCEL_TIMER_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Clear active session from database
    yield call(teamDatabase.clearActiveTimerSession, teamId, userId);

    // Remove start timestamp from localStorage
    localStorage.removeItem(`timer_start_${teamId}_${userId}`);

    yield put({
      type: timerActionTypes.CANCEL_TIMER_SUCCESS,
      payload: { message: 'Timer cancelled successfully' },
    });

    // Refresh punch status to update activeSession state in Redux
    yield put({
      type: timerActionTypes.CHECK_PUNCH_STATUS,
      payload: { teamId, userId },
    });

    console.log('Timer cancelled successfully');
    addNotification({
      message: 'Timer cancelled successfully',
      level: notificationStates.warning,
    });
  } catch (error) {
    console.error('Error cancelling timer:', error);
    yield put({
      type: timerActionTypes.CANCEL_TIMER_FAILED,
      payload: { error: 'Failed to cancel timer' },
    });
  }
}

// Fetch Projects Saga
export function* fetchProjectsSaga({ payload }) {
  yield put({ type: timerActionTypes.FETCH_PROJECTS_PENDING });

  try {
    const { companyId } = payload;

    if (!companyId) {
      const errorMessage = 'Company ID is required to fetch projects';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.FETCH_PROJECTS_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    const response = yield call(teamDatabase.getActiveProjects, companyId);

    if (response.success) {
      yield put({
        type: timerActionTypes.FETCH_PROJECTS_SUCCESS,
        payload: { projects: response.data },
      });

      console.log('Projects fetched successfully:', response.data.length);
    } else {
      const errorMessage = response.error || 'Failed to fetch projects';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.FETCH_PROJECTS_FAILED,
        payload: { error: errorMessage },
      });
    }
  } catch (error) {
    console.error('Error fetching projects:', error);
    const errorMessage = 'Failed to fetch projects. Please try again.';
    addNotification({
      message: errorMessage,
      level: notificationStates.error,
    });
    yield put({
      type: timerActionTypes.FETCH_PROJECTS_FAILED,
      payload: { error: errorMessage },
    });
  }
}

// Cross-platform session validation saga
export function* checkCrossPlatformSessionSaga({ payload }) {
  try {
    const { teamId, userId } = payload;

    if (!teamId || !userId) {
      yield put({
        type: timerActionTypes.CHECK_CROSS_PLATFORM_SESSION_FAILED,
        payload: { error: 'Invalid team or user data' },
      });
      return;
    }

    const response = yield call(
      teamDatabase.checkExistingActiveSession,
      teamId,
      userId
    );

    if (response.success) {
      if (response.exists && response.data) {
        const existingSession = response.data;

        // Check if the existing session is from a different platform
        if (existingSession.source && existingSession.source !== 'web') {
          const errorMessage = `You cannot start timer as it is already running in ${
            existingSession.source === 'desktop'
              ? 'desktop app'
              : 'another platform'
          }`;

          addNotification({
            message: errorMessage,
            level: notificationStates.error,
          });

          yield put({
            type: timerActionTypes.CHECK_CROSS_PLATFORM_SESSION_FAILED,
            payload: {
              error: errorMessage,
              existingSession: existingSession,
            },
          });
          return;
        }
      }

      // No conflict found or session is from same platform
      yield put({
        type: timerActionTypes.CHECK_CROSS_PLATFORM_SESSION_SUCCESS,
        payload: {
          existingSession: response.data,
          exists: response.exists,
        },
      });
    } else {
      // Database check failed - BLOCK operation as requested
      const errorMessage =
        'Unable to verify timer status. Please check your connection and try again.';

      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });

      yield put({
        type: timerActionTypes.CHECK_CROSS_PLATFORM_SESSION_FAILED,
        payload: { error: errorMessage },
      });
    }
  } catch (error) {
    console.error('Error checking cross-platform session:', error);
    const errorMessage =
      'Unable to verify timer status. Please check your connection and try again.';

    addNotification({
      message: errorMessage,
      level: notificationStates.error,
    });

    yield put({
      type: timerActionTypes.CHECK_CROSS_PLATFORM_SESSION_FAILED,
      payload: { error: errorMessage },
    });
  }
}

// Fetch Tasks Saga
export function* fetchTasksSaga({ payload }) {
  yield put({ type: timerActionTypes.FETCH_TASKS_PENDING });

  try {
    const { projectId, userId, companyId } = payload;

    if (!projectId || !userId || !companyId) {
      const errorMessage =
        'Project ID, User ID, and Company ID are required to fetch tasks';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.FETCH_TASKS_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    const response = yield call(
      teamDatabase.getProjectTasks,
      projectId,
      userId,
      companyId
    );

    if (response.success) {
      yield put({
        type: timerActionTypes.FETCH_TASKS_SUCCESS,
        payload: { tasks: response.data },
      });

      console.log('Tasks fetched successfully:', response.data.length);
    } else {
      const errorMessage = response.error || 'Failed to fetch tasks';
      addNotification({
        message: errorMessage,
        level: notificationStates.warning,
      });
      yield put({
        type: timerActionTypes.FETCH_TASKS_FAILED,
        payload: { error: errorMessage },
      });
    }
  } catch (error) {
    console.error('Error fetching tasks:', error);
    const errorMessage = 'Failed to fetch tasks. Please try again.';
    addNotification({
      message: errorMessage,
      level: notificationStates.error,
    });
    yield put({
      type: timerActionTypes.FETCH_TASKS_FAILED,
      payload: { error: errorMessage },
    });
  }
}

// Punch In Saga
export function* punchInSaga({ payload }) {
  yield put({ type: timerActionTypes.PUNCH_IN_PENDING });

  try {
    const { teamId, userId, companyId } = payload;

    console.log('🔴 PUNCH_IN: Starting punch in process', {
      teamId,
      userId,
      companyId,
      timestamp: new Date().toISOString(),
    });

    if (!teamId || !userId) {
      const errorMessage =
        'Unable to punch in. Please ensure you are logged in and have selected a team.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.PUNCH_IN_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Get user timezone from database - NEW: No fallback to browser
    console.log('🔴 PUNCH_IN: Getting user timezone from database');
    let userTimezoneInfo;
    try {
      userTimezoneInfo = yield call(getUserTimezone, teamId, userId);
      console.log('🔴 PUNCH_IN: Timezone retrieved from database', {
        timezone: userTimezoneInfo.timezone,
        source: userTimezoneInfo.source,
      });
    } catch (error) {
      const errorMessage = `Unable to get user timezone: ${error.message}`;
      console.error('🔴 PUNCH_IN: Timezone error', { error: error.message });
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.PUNCH_IN_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Check if user is already punched in
    const statusCheck = yield call(
      teamDatabase.getCurrentPunchStatus,
      teamId,
      userId
    );

    if (
      statusCheck.success &&
      statusCheck.data &&
      statusCheck.data.lastPunchinoutStatus === 'punchin'
    ) {
      const errorMessage = 'You are already punched in.';
      addNotification({
        message: errorMessage,
        level: notificationStates.warning,
      });
      yield put({
        type: timerActionTypes.PUNCH_IN_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Create punch in data
    const punchTime = yield call(getServerTime);
    const punchData = {
      state: 'punchin',
      timeZone: userTimezoneInfo.timezone, // Use database timezone
      utc: punchTime.toUTCString(),
    };

    console.log('🔴 PUNCH_IN: Creating punch data with database timezone', {
      punchTime: punchTime.toISOString(),
      timezone: userTimezoneInfo.timezone,
      punchData,
    });

    // Save punch in data
    const response = yield call(
      teamDatabase.savePunchData,
      teamId,
      userId,
      punchTime,
      punchData,
      companyId
    );

    if (response.success) {
      yield put({
        type: timerActionTypes.PUNCH_IN_SUCCESS,
        payload: {
          punchData: {
            ...punchData,
            punchTime: punchTime.toISOString(),
          },
        },
      });

      // Refresh punch status to update UI
      yield put({
        type: timerActionTypes.CHECK_PUNCH_STATUS,
        payload: { teamId, userId },
      });

      console.log('Punch in successful');
      addNotification({
        message: 'Punched in successfully!',
        level: notificationStates.success,
      });
    } else {
      const errorMessage = 'Failed to punch in. Please try again.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.PUNCH_IN_FAILED,
        payload: { error: errorMessage },
      });
    }
  } catch (error) {
    console.error('Error punching in:', error);
    const errorMessage = 'Failed to punch in. Please try again.';
    addNotification({
      message: errorMessage,
      level: notificationStates.error,
    });
    yield put({
      type: timerActionTypes.PUNCH_IN_FAILED,
      payload: { error: errorMessage },
    });
  }
}

// Punch Out Saga
export function* punchOutSaga({ payload }) {
  yield put({ type: timerActionTypes.PUNCH_OUT_PENDING });

  try {
    const { teamId, userId, companyId } = payload;

    console.log('🔴 PUNCH_OUT: Starting punch out process', {
      teamId,
      userId,
      companyId,
      timestamp: new Date().toISOString(),
    });

    if (!teamId || !userId) {
      const errorMessage =
        'Unable to punch out. Please ensure you are logged in and have selected a team.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.PUNCH_OUT_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Get user timezone from database - NEW: No fallback to browser
    console.log('🔴 PUNCH_OUT: Getting user timezone from database');
    let userTimezoneInfo;
    try {
      userTimezoneInfo = yield call(getUserTimezone, teamId, userId);
      console.log('🔴 PUNCH_OUT: Timezone retrieved from database', {
        timezone: userTimezoneInfo.timezone,
        source: userTimezoneInfo.source,
      });
    } catch (error) {
      const errorMessage = `Unable to get user timezone: ${error.message}`;
      console.error('🔴 PUNCH_OUT: Timezone error', { error: error.message });
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.PUNCH_OUT_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Check if timer is currently running
    const timerState = yield select((state) => state.timer);
    if (timerState.isPlaying) {
      const errorMessage = 'Please stop the timer before punching out.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.PUNCH_OUT_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Check if user is actually punched in
    const statusCheck = yield call(
      teamDatabase.getCurrentPunchStatus,
      teamId,
      userId
    );

    if (
      !statusCheck.success ||
      !statusCheck.data ||
      statusCheck.data.lastPunchinoutStatus !== 'punchin'
    ) {
      const errorMessage = 'You are not currently punched in.';
      addNotification({
        message: errorMessage,
        level: notificationStates.warning,
      });
      yield put({
        type: timerActionTypes.PUNCH_OUT_FAILED,
        payload: { error: errorMessage },
      });
      return;
    }

    // Create punch out data
    const punchTime = yield call(getServerTime);
    const punchData = {
      state: 'punchout',
      timeZone: userTimezoneInfo.timezone, // Use database timezone
      utc: punchTime.toUTCString(),
    };

    console.log('🔴 PUNCH_OUT: Creating punch data with database timezone', {
      punchTime: punchTime.toISOString(),
      timezone: userTimezoneInfo.timezone,
      punchData,
    });

    // Save punch out data
    const response = yield call(
      teamDatabase.savePunchData,
      teamId,
      userId,
      punchTime,
      punchData,
      companyId
    );

    if (response.success) {
      yield put({
        type: timerActionTypes.PUNCH_OUT_SUCCESS,
        payload: {
          punchData: {
            ...punchData,
            punchTime: punchTime.toISOString(),
          },
        },
      });

      // Refresh punch status to update UI
      yield put({
        type: timerActionTypes.CHECK_PUNCH_STATUS,
        payload: { teamId, userId },
      });

      console.log('Punch out successful');
      addNotification({
        message: 'Punched out successfully!',
        level: notificationStates.success,
      });
    } else {
      const errorMessage = 'Failed to punch out. Please try again.';
      addNotification({
        message: errorMessage,
        level: notificationStates.error,
      });
      yield put({
        type: timerActionTypes.PUNCH_OUT_FAILED,
        payload: { error: errorMessage },
      });
    }
  } catch (error) {
    console.error('Error punching out:', error);
    const errorMessage = 'Failed to punch out. Please try again.';
    addNotification({
      message: errorMessage,
      level: notificationStates.error,
    });
    yield put({
      type: timerActionTypes.PUNCH_OUT_FAILED,
      payload: { error: errorMessage },
    });
  }
}

// Check Punch Status Saga - NEW: Implements separate logic for timer controls vs punch button
export function* checkPunchStatusSaga({ payload }) {
  yield put({ type: timerActionTypes.CHECK_PUNCH_STATUS_PENDING });

  try {
    const { teamId, userId } = payload;

    if (!teamId || !userId) {
      yield put({
        type: timerActionTypes.CHECK_PUNCH_STATUS_FAILED,
        payload: { error: 'Invalid team or user data' },
      });
      return;
    }

    // Check for active session first (affects timer controls)
    const activeSessionResponse = yield call(
      teamDatabase.getActiveTimerSession,
      teamId,
      userId
    );

    // Check daily punch status (affects punch button display)
    const dailyPunchResponse = yield call(
      teamDatabase.getCurrentPunchStatus,
      teamId,
      userId
    );

    console.log('WebTimeTracking : Daily punch response:', dailyPunchResponse);

    // Calculate effective punch status for timer controls
    const hasActiveSession =
      activeSessionResponse.success && activeSessionResponse.exists;
    const dailyIsPunchedIn =
      dailyPunchResponse.success &&
      dailyPunchResponse.data?.lastPunchinoutStatus === 'punchin';

    console.log('WebTimeTracking : Calculated punch statuses:', {
      hasActiveSession,
      dailyIsPunchedIn,
      dbLastPunchinoutStatus: dailyPunchResponse.data?.lastPunchinoutStatus,
    });

    // Timer controls: enabled if active session exists OR daily punched in
    const timerControlsEnabled = hasActiveSession || dailyIsPunchedIn;

    // Punch button: shows daily status only (independent of active session)
    const punchButtonStatus = dailyIsPunchedIn ? 'punchin' : 'punchout';

    const successPayload = {
      // For timer controls (start/stop/cancel)
      isPunchedIn: timerControlsEnabled,

      // For punch button display (separate from timer controls)
      dailyPunchStatus: {
        isPunchedIn: dailyIsPunchedIn,
        lastPunchinoutStatus:
          dailyPunchResponse.data?.lastPunchinoutStatus || 'punchout',
        currentPunchSession: dailyIsPunchedIn ? dailyPunchResponse.data : null,
      },

      // Active session info (for debugging and UI logic)
      activeSession: {
        exists: hasActiveSession,
        data: hasActiveSession ? activeSessionResponse.data : null,
      },

      // Legacy fields for backward compatibility
      lastPunchinoutStatus: punchButtonStatus,
      currentPunchSession: dailyIsPunchedIn ? dailyPunchResponse.data : null,
    };

    console.log(
      'WebTimeTracking : About to dispatch CHECK_PUNCH_STATUS_SUCCESS with:',
      {
        timerControlsEnabled,
        dailyPunchStatus: punchButtonStatus,
        hasActiveSession,
        dailyIsPunchedIn,
      }
    );

    yield put({
      type: timerActionTypes.CHECK_PUNCH_STATUS_SUCCESS,
      payload: successPayload,
    });

    console.log('Punch status checked successfully:', {
      timerControlsEnabled,
      dailyPunchStatus: punchButtonStatus,
      hasActiveSession,
    });
  } catch (error) {
    console.error('Error checking punch status:', error);

    // Fallback to safe defaults
    const defaultPayload = {
      isPunchedIn: false,
      dailyPunchStatus: {
        isPunchedIn: false,
        lastPunchinoutStatus: 'punchout',
        currentPunchSession: null,
      },
      activeSession: {
        exists: false,
        data: null,
      },
      lastPunchinoutStatus: 'punchout',
      currentPunchSession: null,
    };

    yield put({
      type: timerActionTypes.CHECK_PUNCH_STATUS_SUCCESS,
      payload: defaultPayload,
    });
  }
}

// Root saga
export default function* timerWatcher() {
  yield all([
    takeEvery(timerActionTypes.START_TIMER, startTimerSaga),
    takeEvery(timerActionTypes.STOP_TIMER, stopTimerSaga),
    takeEvery(timerActionTypes.RESUME_TIMER, resumeTimerSaga),
    takeEvery(
      timerActionTypes.RESUME_TIMER_ON_TAB_CHANGE,
      resumeTimerOnTabChangeSaga
    ),
    takeEvery(timerActionTypes.CANCEL_TIMER, cancelTimerSaga),
    takeEvery(timerActionTypes.PUNCH_IN, punchInSaga),
    takeEvery(timerActionTypes.PUNCH_OUT, punchOutSaga),
    takeEvery(timerActionTypes.CHECK_PUNCH_STATUS, checkPunchStatusSaga),
    takeEvery(timerActionTypes.FETCH_PROJECTS, fetchProjectsSaga),
    takeEvery(timerActionTypes.FETCH_TASKS, fetchTasksSaga),
    takeEvery(
      timerActionTypes.CHECK_CROSS_PLATFORM_SESSION,
      checkCrossPlatformSessionSaga
    ),
  ]);
}
